import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { CharacterCountBar } from "./CharacterCountBar";
import { META_LIMITS } from "./seoCharLimits";
import { FileText, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { technicalAuditService } from "@/services/technicalAuditService";
import { readMultipleMetaPreview } from "@/types/technicalAudit";
import type {
	PageIssue,
	MultipleMetaDetails,
	MultipleMetaPreviewPayload,
	MultipleMetaRecommendedSource,
	ProposedFix,
} from "@/types/technicalAudit";

interface MultipleMetaCardProps {
	issue: PageIssue;
	/** Lift the chosen single description up to the drawer so Apply writes
	 *  exactly what's shown here (WYSIWYG). */
	onEdit?: (fix: ProposedFix) => void;
}

// Short keep/remove sentence for which existing tag the chosen value keeps.
const chosenSourceLabel = (
	source?: MultipleMetaRecommendedSource,
): string => {
	if (source === "meta_1") return "Keep tag #1; remove the other(s).";
	if (source === "meta_2") return "Keep tag #2; remove the other(s).";
	if (source === "synthesized")
		return "New combined description — replace all existing tags with this one.";
	if (source === "operator")
		return "Your edited description — replaces all existing tags with this one.";
	return "Keep this one description and remove the duplicate tag(s).";
};

interface StoredRecommendation {
	description: string;
	source?: MultipleMetaRecommendedSource;
	reason: string | null;
}

// A persisted single-meta recommendation already stored on the issue
// (approved_value / proposed_fix / proposed_value — Generate fix persists to
// proposed_value as a JSON string). When present it seeds the editor instantly
// and the card skips the auto-preview LLM call entirely.
function readPersistedRecommendation(
	issue: PageIssue,
): StoredRecommendation | null {
	const sources = [issue.approved_value, issue.proposed_fix, issue.proposed_value];
	for (const raw of sources) {
		if (!raw) continue;
		let obj: Record<string, unknown> | null = null;
		if (typeof raw === "string") {
			try {
				const parsed = JSON.parse(raw);
				obj =
					parsed && typeof parsed === "object" && !Array.isArray(parsed)
						? (parsed as Record<string, unknown>)
						: null;
			} catch {
				obj = null;
			}
		} else if (typeof raw === "object" && !Array.isArray(raw)) {
			obj = raw as unknown as Record<string, unknown>;
		}
		const value = obj?.recommended_meta_description;
		if (typeof value === "string" && value.trim().length > 0) {
			const source = obj?.recommended_source;
			const reason = obj?.recommendation_reason;
			return {
				description: value,
				source: typeof source === "string" ? source : undefined,
				reason:
					typeof reason === "string" && reason.trim().length > 0 ? reason : null,
			};
		}
	}
	return null;
}

export function MultipleMetaCard({ issue, onEdit }: MultipleMetaCardProps) {
	const details = issue.issue_details as MultipleMetaDetails;
	const persisted = useMemo(
		() => readPersistedRecommendation(issue),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[issue.id, issue.approved_value, issue.proposed_fix, issue.proposed_value],
	);
	const [preview, setPreview] = useState<MultipleMetaPreviewPayload | null>(
		null,
	);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// Auto-load the LLM single-meta recommendation ONLY when nothing is stored
	// yet — Generate fix persists its suggestion to the issue, so re-previewing
	// on every drawer open would double-spend the LLM for no new information.
	const hasPersisted = Boolean(persisted);
	useEffect(() => {
		if (hasPersisted) return;
		let ignore = false;
		setIsLoading(true);
		setError(null);
		setPreview(null);

		technicalAuditService
			.previewFix(issue.id)
			.then((data) => {
				if (ignore) return;
				setPreview(readMultipleMetaPreview(data.planned_payload));
			})
			.catch((err: unknown) => {
				if (ignore) return;
				const detail =
					(
						err as {
							response?: { data?: { detail?: string } };
							message?: string;
						}
					).response?.data?.detail ||
					(err as { message?: string }).message ||
					"Failed to load the recommended description";
				setError(detail);
			})
			.finally(() => {
				if (!ignore) setIsLoading(false);
			});

		return () => {
			ignore = true;
		};
	}, [issue.id, hasPersisted]);

	// Prefer the descriptions the recommendation reasoned over; fall back to the
	// issue's recorded tags so the pickable list always renders.
	const listedDescriptions =
		preview?.current_meta_descriptions &&
		preview.current_meta_descriptions.length > 0
			? preview.current_meta_descriptions
			: (details.meta_descriptions ?? []);

	// Seed: a persisted suggestion (Generate fix / earlier approval) wins over
	// the live preview.
	const seedDescription =
		persisted?.description ?? preview?.recommended_meta_description ?? "";
	const seedSource = persisted ? persisted.source : preview?.recommended_source;
	const seedReason = persisted
		? persisted.reason
		: (preview?.recommendation_reason ?? null);

	// SeoMetaEditCard idiom: the draft belongs to one seed generation. A new
	// seed (regenerated fix, preview arriving) discards the stale draft.
	const draftKey = `${issue.id}:${seedDescription}:${seedSource ?? ""}`;
	const [draft, setDraft] = useState<{
		key: string;
		text: string;
		source?: MultipleMetaRecommendedSource;
	} | null>(null);
	const chosenText = draft?.key === draftKey ? draft.text : seedDescription;
	const chosenSource = draft?.key === draftKey ? draft.source : seedSource;
	const isSeedValue = chosenText.trim() === seedDescription.trim();

	// The keep/remove marks follow the CURRENT chosen text, so picking tag #2 or
	// hand-editing immediately re-labels the list.
	const keptIndex =
		chosenText.trim().length > 0
			? listedDescriptions.findIndex((d) => d.trim() === chosenText.trim())
			: -1;

	const pick = (idx: number) => {
		setDraft({
			key: draftKey,
			text: listedDescriptions[idx],
			source: idx === 0 ? "meta_1" : idx === 1 ? "meta_2" : "operator",
		});
	};

	const editText = (value: string) => {
		const trimmed = value.trim();
		const idx = listedDescriptions.findIndex((d) => d.trim() === trimmed);
		setDraft({
			key: draftKey,
			text: value,
			source:
				idx === 0
					? "meta_1"
					: idx === 1
						? "meta_2"
						: trimmed === seedDescription.trim() && trimmed.length > 0
							? seedSource
							: "operator",
		});
	};

	// Push the choice up so Apply writes exactly what the operator sees. An
	// empty value is lifted too — the drawer treats it as "no meaningful fix"
	// and disables Apply, so a blank card can never trigger a silent
	// server-side pick.
	useEffect(() => {
		const trimmed = chosenText.trim();
		if (!trimmed) {
			onEdit?.({ recommended_meta_description: "" });
			return;
		}
		onEdit?.({
			recommended_meta_description: trimmed,
			recommended_source: chosenSource,
			recommendation_reason: isSeedValue ? (seedReason ?? undefined) : undefined,
		});
	}, [chosenText, chosenSource, isSeedValue, seedReason, onEdit]);

	return (
		<div className="space-y-4">
			<div className="flex items-start gap-3 rounded-xl p-4 border bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800">
				<div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-amber-100 dark:bg-amber-900/40">
					<AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
				</div>
				<div className="flex-1">
					<h4 className="font-semibold text-amber-900 dark:text-amber-300">Multiple Meta Descriptions</h4>
					<p className="text-sm mt-1 text-amber-700 dark:text-amber-400">
						This page has {details.count} meta descriptions. Only one should be present; search engines may pick the wrong one.
					</p>
				</div>
				<Badge variant="outline" className="bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800">
					{details.count} found
				</Badge>
			</div>

			<div
				data-testid="multiple-meta-recommendation"
				className="rounded-xl border border-teal/40 bg-teal-light p-4"
			>
				<div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-teal-dark">
					<CheckCircle2 className="h-4 w-4" />
					Single meta description to keep (editable)
				</div>
				{isLoading && !chosenText ? (
					<div className="mt-2 flex items-center gap-2 text-sm text-teal-dark">
						<Loader2 className="h-4 w-4 animate-spin" />
						Loading the recommended description…
					</div>
				) : (
					<>
						{!chosenText.trim() && (
							<p className="mt-2 text-sm text-foreground/70">
								{error
									? "Couldn't load a recommendation automatically. Click Generate fix above, pick one of the descriptions below, or write your own — Apply stays disabled until a description is chosen."
									: "No recommendation yet. Click Generate fix above, pick one of the descriptions below, or write your own."}
							</p>
						)}
						<Textarea
							data-testid="multiple-meta-editor"
							value={chosenText}
							onChange={(e) => editText(e.target.value)}
							className="mt-2 min-h-[90px] bg-background"
							placeholder="The single meta description to keep…"
						/>
						<div className="mt-2">
							<CharacterCountBar
								current={chosenText.trim().length}
								min={META_LIMITS.min}
								max={META_LIMITS.max}
								optimal={META_LIMITS.optimal}
							/>
						</div>
						{chosenText.trim() && (
							<div
								data-testid="multiple-meta-source-label"
								className="mt-2 text-xs text-teal-dark"
							>
								{chosenSourceLabel(chosenSource)}
							</div>
						)}
						{isSeedValue && seedReason && (
							<p className="mt-2 text-xs text-foreground/70">
								<span className="font-medium">Why:</span> {seedReason}
							</p>
						)}
						<p className="mt-2 text-xs text-teal-dark">
							Apply writes this to the post excerpt and the SEO-plugin meta.
						</p>
					</>
				)}
			</div>

			<Card className="border-border shadow-sm">
				<CardHeader className="pb-3">
					<CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
						<div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center">
							<FileText className="w-4 h-4 text-blue-600 dark:text-blue-400" />
						</div>
						Meta Descriptions Found
					</CardTitle>
					<p className="text-xs text-muted-foreground">
						Pick the description to keep, or edit the text above.
					</p>
				</CardHeader>
				<CardContent className="pt-0 space-y-2">
					{listedDescriptions.map((desc, idx) => {
						const isKept = keptIndex === idx;
						const showKeepMark = keptIndex !== -1;
						return (
							<div
								key={`${idx}-${desc.slice(0, 24)}`}
								className={`p-3 rounded-lg border ${
									isKept
										? "border-teal/40 bg-teal-light/40"
										: "bg-muted border-border"
								}`}
							>
								<div className="flex items-start gap-2">
									<Badge
										variant="outline"
										className={`shrink-0 text-xs ${
											isKept
												? "border-teal/40 bg-teal-light text-teal-dark"
												: "bg-muted text-muted-foreground border-border"
										}`}
									>
										#{idx + 1}
										{showKeepMark ? (isKept ? " · keep" : " · remove") : ""}
									</Badge>
									<p className="flex-1 text-sm text-foreground">{desc}</p>
									<Button
										type="button"
										variant="outline"
										size="sm"
										data-testid={`multiple-meta-pick-${idx}`}
										className="shrink-0"
										disabled={isKept}
										onClick={() => pick(idx)}
									>
										{isKept ? "Selected" : "Use this one"}
									</Button>
								</div>
							</div>
						);
					})}
				</CardContent>
			</Card>
		</div>
	);
}
